import { ContactModal } from "../../components/ContactModal";
import { FileText, X, Download } from "lucide-react";
import { api } from "../../lib/api";
import { AwardModal } from "../../components/AwardModal";
import { fmtSize } from "./model";
import { BidForm } from "./BidForm";
import { BidTermsModal } from "./BidTermsModal";
import { type ProjectDetailView } from "./viewTypes";

export function ProjectDetailDialogs({ view }: { view: ProjectDetailView }) {
  const { id, contactOpen, setContactOpen, busy, err, awardTarget, setAwardTarget, showAllFiles, setShowAllFiles, termsTarget, setTermsTarget, documentsTarget, setDocumentsTarget, attachments, form, setForm, bidFiles, setBidFiles, bidComposerOpen, setBidComposerOpen, legalTerms, legalTermsState, bidReviewConfirmed, setBidReviewConfirmed, p, isOwner, myBid, canEditBid, activeCompanyContext, selectedBidCompany, selectedBidderName, submitBid, award, downloadBidDocument, removeBidDocument } = view;
  return <>
{contactOpen && p.ownerUserId != null && (
        <ContactModal
          open onClose={() => setContactOpen(false)}
          toUserId={p.ownerUserId} toName={p.clientSummary?.name || "Project client"} contextType="project" contextId={id}
          subject={p.title}
          starter={`Hi, I'm reaching out about your project "${p.title}".`}
        />
      )}

{bidComposerOpen && !isOwner && canEditBid && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-navy/60 p-4" onClick={() => { if (!busy) setBidComposerOpen(false); }}>
          <div role="dialog" aria-modal="true" aria-label={myBid ? "Revise sealed bid" : "Place a sealed bid"} className="card w-full max-w-lg max-h-[90vh] overflow-y-auto p-5" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div><h2 className="text-lg font-bold text-navy">{myBid ? "Revise your sealed bid" : "Place a sealed bid"}</h2><p className="text-xs text-mut mt-1">Only you and the project owner can see your proposal.</p></div>
              <button type="button" className="btn-outline h-8 px-2" disabled={busy} onClick={() => setBidComposerOpen(false)} aria-label="Close bid form"><X className="w-4 h-4" /></button>
            </div>
            {err && <p role="alert" className="mt-3 text-sm text-brand-red">{err}</p>}
            <BidForm form={form} setForm={setForm} busy={busy} onSubmit={submitBid} submitLabel={myBid ? "Update Sealed Bid" : "Submit Sealed Bid"} bidderName={selectedBidderName} bidderAvailable={!activeCompanyContext || !!selectedBidCompany} legalTerms={legalTerms} legalTermsState={legalTermsState} reviewConfirmed={bidReviewConfirmed} onReviewConfirmed={setBidReviewConfirmed} files={bidFiles} onFilesChange={setBidFiles} existingDocuments={myBid?.documents || []} onRemoveDocument={removeBidDocument} />
          </div>
        </div>
      )}

{showAllFiles && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setShowAllFiles(false)}>
          <div role="dialog" aria-modal="true" aria-label="Project attachments" className="card w-full max-w-3xl max-h-[85vh] overflow-y-auto p-5" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 mb-4"><h2 className="text-lg font-bold text-navy">Project attachments ({attachments.length})</h2><button type="button" className="btn-outline h-8 px-2" onClick={() => setShowAllFiles(false)} aria-label="Close attachments"><X className="w-4 h-4" /></button></div>
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {attachments.map((attachment) => <li key={attachment.id}>
                <a href={api.attachmentDownloadUrl(id, attachment.id)} download={attachment.filename} className="block rounded-lg border border-line p-3 text-navy hover:border-navy-500 min-w-0">
                  <FileText className="w-6 h-6 mb-2" /><span className="block text-sm font-semibold truncate">{attachment.filename}</span><span className="block text-xs text-mut">{fmtSize(attachment.size)}</span>
                </a>
              </li>)}
            </ul>
          </div>
        </div>
      )}

{awardTarget && (
        <AwardModal
          negotiation
          name={awardTarget.bidderName}
          allowCompletionDate
          total={awardTarget.finalPrice ?? awardTarget.standingPrice ?? awardTarget.price ?? 0}
          terms={awardTarget.terms}
          busy={busy}
          onClose={() => setAwardTarget(null)}
          onPreview={opts => api.previewProjectAward(id, awardTarget.id, opts)}
          onConfirm={(opts) => award(awardTarget.id, opts)}
        />
      )}

{termsTarget && (
        <BidTermsModal
          bidderName={termsTarget.bidderName}
          terms={termsTarget.terms}
          onClose={() => {
            const trigger = termsTarget.trigger;
            setTermsTarget(null);
            requestAnimationFrame(() => trigger.focus());
          }}
        />
      )}

{documentsTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setDocumentsTarget(null)}>
          <div role="dialog" aria-modal="true" aria-label={`Documents from ${documentsTarget.bidderName}`} className="card w-full max-w-2xl p-5" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="font-bold text-navy">Documents from {documentsTarget.bidderName}</h2>
              <button type="button" className="btn-outline h-8 px-2" aria-label="Close bidder documents" onClick={() => setDocumentsTarget(null)}><X className="w-4 h-4" /></button>
            </div>
            <p className="mb-3 text-xs text-mut">Select a file to download it.</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {documentsTarget.documents.map((document) => (
                <button key={document.id} type="button" className="flex min-w-0 items-start gap-2 rounded-lg border border-line p-3 text-left text-sm text-navy hover:border-brand-blue"
                  onClick={() => void downloadBidDocument(documentsTarget.bidId, document)}>
                  <FileText className="w-5 h-5 shrink-0" /><span className="min-w-0 flex-1"><span className="block break-words text-xs font-semibold">{document.filename}</span><span className="text-[11px] text-mut">{fmtSize(document.size)}</span></span><Download className="w-4 h-4 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
</>;
}
